import type { ServerWidgetConfig } from "./ConfigurationService";
import { parsePublicWidgetConfig } from "./parsePublicWidgetConfig";

export const DEFAULT_PUBLIC_CONFIG_TIMEOUT_MS = 3_000;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export type FetchPublicWidgetConfigOptions = {
  timeoutMs?: number;
  /** External abort (e.g. React effect cleanup). */
  signal?: AbortSignal;
};

/**
 * Load public runtime config for a widgetId (no session).
 * Returns null on 404/network/timeout/invalid body — caller keeps embed-only config.
 */
export async function fetchPublicWidgetConfig(
  apiUrl: string,
  widgetId: string,
  options: FetchPublicWidgetConfigOptions = {}
): Promise<ServerWidgetConfig | null> {
  if (!widgetId) return null;

  const timeoutMs = options.timeoutMs ?? DEFAULT_PUBLIC_CONFIG_TIMEOUT_MS;
  const url = `${apiUrl.replace(/\/$/, "")}/api/widget/config?widgetId=${encodeURIComponent(
    widgetId
  )}`;

  const controller = new AbortController();
  const onExternalAbort = () => controller.abort();
  if (options.signal) {
    if (options.signal.aborted) {
      return null;
    }
    options.signal.addEventListener("abort", onExternalAbort, { once: true });
  }

  const timer = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const res = await fetch(url, { signal: controller.signal });
    if (!res.ok) return null;
    const body: unknown = await res.json();
    if (!isRecord(body)) return null;
    return parsePublicWidgetConfig(body.widgetConfig);
  } catch {
    // AbortError, network, parse — all fail-open to embed.
    return null;
  } finally {
    clearTimeout(timer);
    if (options.signal) {
      options.signal.removeEventListener("abort", onExternalAbort);
    }
  }
}
